<template>
    <section class="border rounded p-3 mb-4" aria-label="Расписание блюда">
        <h5>Расписание блюда</h5>
        <p class="text-muted mb-2">Время московское (МСК). Блюдо доступно в любом из указанных интервалов.</p>
        <p class="text-warning mb-3">Следующий импорт iiko заменит это расписание данными из iiko.</p>

        <fieldset :disabled="isSaving" class="border-0 p-0 m-0">
            <div v-if="!draftSchedules.length" class="alert alert-light border">
                Без ограничений по расписанию блюда. Добавьте интервал, чтобы ограничить доступность.
            </div>
            <div
                v-for="(schedule, index) in draftSchedules"
                :key="schedule.localId"
                class="border rounded p-3 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong>Интервал {{ index + 1 }}</strong>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        :aria-label="`Удалить интервал ${index + 1}`"
                        @click="removeSchedule(index)">Удалить</button>
                </div>
                <div class="form-row">
                    <div class="form-group col-sm-6">
                        <label :for="`dish-slot-from-${schedule.localId}`">С</label>
                        <input
                            :id="`dish-slot-from-${schedule.localId}`"
                            v-model.trim="schedule.from"
                            type="text"
                            class="form-control"
                            placeholder="09:00"
                            maxlength="8"
                            @input="clearMessage" />
                    </div>
                    <div class="form-group col-sm-6">
                        <label :for="`dish-slot-to-${schedule.localId}`">До</label>
                        <input
                            :id="`dish-slot-to-${schedule.localId}`"
                            v-model.trim="schedule.to"
                            type="text"
                            class="form-control"
                            placeholder="19:30"
                            maxlength="8"
                            @input="clearMessage" />
                    </div>
                </div>
                <div class="mb-2">Дни недели</div>
                <div class="d-flex flex-wrap">
                    <label
                        v-for="day in weekDays"
                        :key="day.value"
                        class="mr-3">
                        <input
                            v-model="schedule.week_days"
                            type="checkbox"
                            :value="day.value"
                            class="mr-1"
                            @change="clearMessage" />{{ day.label }}
                    </label>
                </div>
                <small v-if="isOvernight(schedule)" class="form-text text-muted">
                    Окончание — на следующий день. Выберите дни начала интервала.
                </small>
            </div>

            <small class="form-text text-muted mb-3">
                Формат времени — ЧЧ:ММ или ЧЧ:ММ:СС. Полные сутки — 00:00–24:00. Время окончания не включается.
            </small>
            <div class="d-flex flex-wrap">
                <button
                    type="button"
                    class="btn btn-outline-primary mr-2 mb-2"
                    :disabled="draftSchedules.length >= 100"
                    @click="addSchedule">Добавить интервал</button>
                <button
                    v-if="draftSchedules.length"
                    type="button"
                    class="btn btn-outline-danger mr-2 mb-2"
                    @click="clearSchedules">Удалить все интервалы</button>
            </div>
            <div
                v-if="error"
                class="alert alert-danger mt-2"
                role="alert">{{ error }}</div>
            <div
                v-if="isSaved && !isDirty"
                class="alert alert-success mt-2"
                role="status">Расписание сохранено.</div>
            <div class="d-flex flex-wrap align-items-center mt-2">
                <button
                    type="button"
                    class="btn btn-primary mr-2 mb-2"
                    :disabled="!isDirty"
                    @click="saveSchedules">{{ isSaving ? 'Сохранение…' : 'Сохранить расписание' }}</button>
                <button
                    v-if="isDirty"
                    type="button"
                    class="btn btn-light mb-2"
                    @click="resetSchedules">Отменить изменения расписания</button>
            </div>
            <small class="form-text text-muted">
                Расписание сохраняется отдельно от остальных полей карточки.
                Удаление всех интервалов снимает ограничения после сохранения.
            </small>
        </fieldset>
    </section>
</template>

<script>
    import Vue from 'vue';

    const WEEK_DAYS = [
        { value: 'monday', label: 'Пн' },
        { value: 'tuesday', label: 'Вт' },
        { value: 'wednesday', label: 'Ср' },
        { value: 'thursday', label: 'Чт' },
        { value: 'friday', label: 'Пт' },
        { value: 'saturday', label: 'Сб' },
        { value: 'sunday', label: 'Вс' }
    ];
    const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

    export default {
        name: 'DishScheduleEditor',
        props: {
            restaurantId: { type: Number, required: true },
            dishId: { type: Number, required: true },
            schedules: { type: Array, default: () => [] }
        },
        data() {
            return {
                draftSchedules: [],
                savedSchedules: '[]',
                nextLocalId: 0,
                weekDays: WEEK_DAYS,
                isSaving: false,
                isSaved: false,
                error: ''
            };
        },
        computed: {
            isDirty() {
                return JSON.stringify(this.getPayload()) !== this.savedSchedules;
            }
        },
        watch: {
            schedules: {
                immediate: true,
                handler() {
                    this.resetSchedules();
                }
            }
        },
        methods: {
            formatTime(time) {
                return typeof time === 'string' && time.length === 8 && time.endsWith(':00') ? time.slice(0, 5) : time;
            },
            getPayload() {
                return this.draftSchedules.map(schedule => ({
                    from: schedule.from,
                    to: schedule.to,
                    week_days: [...schedule.week_days]
                }));
            },
            resetSchedules() {
                this.draftSchedules = this.schedules.map(schedule => ({
                    localId: ++this.nextLocalId,
                    from: this.formatTime(schedule.from),
                    to: this.formatTime(schedule.to),
                    week_days: [...(schedule.week_days || [])]
                }));
                this.savedSchedules = JSON.stringify(this.getPayload());
                this.clearMessage();
            },
            clearMessage() {
                this.error = '';
                this.isSaved = false;
            },
            addSchedule() {
                this.draftSchedules.push({
                    localId: ++this.nextLocalId,
                    from: '09:00',
                    to: '19:30',
                    week_days: WEEK_DAYS.map(day => day.value)
                });
                this.clearMessage();
            },
            removeSchedule(index) {
                this.draftSchedules.splice(index, 1);
                this.clearMessage();
            },
            clearSchedules() {
                this.draftSchedules = [];
                this.clearMessage();
            },
            isOvernight(schedule) {
                const from = schedule.from.length === 5 ? `${schedule.from}:00` : schedule.from;
                const to = schedule.to.length === 5 ? `${schedule.to}:00` : schedule.to;
                return TIME_PATTERN.test(from) && TIME_PATTERN.test(to) && from > to;
            },
            validateSchedules() {
                for (const [index, schedule] of this.draftSchedules.entries()) {
                    if (!TIME_PATTERN.test(schedule.from) ||
                        !(TIME_PATTERN.test(schedule.to) || /^24:00(?::00)?$/.test(schedule.to))) {
                        return `Интервал ${index + 1}: укажите корректное время, например 09:00 или 19:30.`;
                    }
                    if (this.formatTime(schedule.from) === this.formatTime(schedule.to)) {
                        return `Интервал ${index + 1}: начало и окончание не должны совпадать. Для полных суток укажите 00:00–24:00.`;
                    }
                    if (!schedule.week_days.length) return `Интервал ${index + 1}: выберите хотя бы один день недели.`;
                }
                return '';
            },
            async saveSchedules() {
                if (this.isSaving || !this.isDirty) return;
                this.error = this.validateSchedules();
                if (this.error) return;
                this.isSaving = true;
                this.isSaved = false;
                try {
                    const response = await Vue.axios.put(`/restaurants/${this.restaurantId}/dishes/${this.dishId}/schedules`, {
                        schedules: this.getPayload()
                    });
                    this.$emit('saved', response.data.schedules);
                    await this.$nextTick();
                    this.savedSchedules = JSON.stringify(this.getPayload());
                    this.isSaved = true;
                } catch (error) {
                    this.error = error.response?.data?.error || error.response?.data?.message || 'Не удалось сохранить расписание. Попробуйте ещё раз.';
                } finally {
                    this.isSaving = false;
                }
            }
        }
    };
</script>
